(() => {
  const propertyName = '_tfb_free_gift';
  const propertyValue = 'true';
  const blockedCartKey = 'tfb-free-gift-blocked-cart-v2';
  const noticeKey = 'tfb-free-gift-notice';
  let syncing = false;
  let queued = false;
  let reconcileTimer;

  const getConfig = () => window.freeGiftConfig || {};

  const isGiftLine = (item) =>
    String(item.properties?.[propertyName]).toLowerCase() === propertyValue;

  const getGiftLines = (cart) => {
    const markedGiftLines = cart.items.filter(isGiftLine);
    if (markedGiftLines.length) return markedGiftLines;

    const variantId = Number(getConfig().variantId);
    if (!variantId || !cart.items.length) return [];

    const cartOnlyContainsGiftVariant = cart.items.every(
      (item) => Number(item.variant_id) === variantId
    );

    return cartOnlyContainsGiftVariant ? cart.items : [];
  };

  const placeGiftRowsLast = () => {
    document.querySelectorAll('.cart-items tbody').forEach((tableBody) => {
      tableBody.querySelectorAll(':scope > .cart-item--free-gift').forEach((giftRow) => {
        if (giftRow.nextElementSibling) tableBody.append(giftRow);
      });
    });
  };

  const scheduleGiftRowPlacement = () => {
    window.requestAnimationFrame(placeGiftRowsLast);
  };

  const scheduleReconcile = (delay = 500) => {
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(reconcile, delay);
  };

  const getCart = async () => {
    const response = await fetch(`${window.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to load the cart.');
    return response.json();
  };

  const getFingerprint = (cart, qualifyingTotal) =>
    JSON.stringify([
      qualifyingTotal,
      ...cart.items
        .filter((item) => !isGiftLine(item))
        .map((item) => [item.key, item.quantity]),
    ]);

  const showNotice = (message) => {
    if (!message) return;

    const existingNotice = document.querySelector('.free-gift-notice');
    if (existingNotice) {
      existingNotice.textContent = message;
      return;
    }

    const container =
      document.querySelector('#main-cart-items .js-contents') ||
      document.querySelector('cart-items .page-width') ||
      document.querySelector('.cart__footer');

    if (!container) {
      sessionStorage.setItem(noticeKey, message);
      return;
    }

    const notice = document.createElement('p');
    notice.className = 'free-gift-notice';
    notice.setAttribute('role', 'status');
    notice.textContent = message;
    container.prepend(notice);
  };

  const restoreNotice = () => {
    const message = sessionStorage.getItem(noticeKey);
    if (!message) return;
    sessionStorage.removeItem(noticeKey);
    showNotice(message);
  };

  const updateLines = async (updates) => {
    const response = await fetch(`${window.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error('Unable to update the promotional item.');
    return response.json();
  };

  const addGift = async (variantId) => {
    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { [propertyName]: propertyValue },
          },
        ],
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'Unable to add the promotional item.');
    }
  };

  const refreshCartUi = async (cart) => {
    if (getConfig().pageType === 'cart') {
      window.location.reload();
      return;
    }

    try {
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const source = html.querySelector('#cart-icon-bubble');
      const target = document.querySelector('#cart-icon-bubble');
      if (source && target) target.replaceWith(source);
    } catch (error) {
      console.error('Free gift cart count refresh failed.', error);
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'free-gift',
        cartData: cart,
        variantId: getConfig().variantId,
      });
    }
  };

  const blockCurrentCart = (fingerprint, message) => {
    sessionStorage.setItem(blockedCartKey, fingerprint);
    sessionStorage.setItem(noticeKey, message);
    showNotice(message);
  };

  const reconcile = async () => {
    const config = getConfig();
    const variantId = Number(config.variantId);
    const threshold = Number(config.threshold);

    if (!window.routes) return;

    if (syncing) {
      queued = true;
      return;
    }

    syncing = true;
    let cartMutated = false;
    let latestCart;

    try {
      const cart = await getCart();
      const giftLines = getGiftLines(cart);

      if (!config.enabled || !variantId || !threshold) {
        if (giftLines.length) {
          const updates = Object.fromEntries(giftLines.map((item) => [item.key, 0]));
          latestCart = await updateLines(updates);
          sessionStorage.removeItem(blockedCartKey);
          await refreshCartUi(latestCart);
        }
        return;
      }

      const giftTotal = giftLines.reduce((total, item) => total + item.final_line_price, 0);
      const qualifyingTotal = cart.total_price - giftTotal;
      const qualifies = qualifyingTotal >= threshold;
      const fingerprint = getFingerprint(cart, qualifyingTotal);
      const blockedFingerprint = sessionStorage.getItem(blockedCartKey);

      if (!qualifies && giftLines.length) {
        const updates = Object.fromEntries(giftLines.map((item) => [item.key, 0]));
        latestCart = await updateLines(updates);
        sessionStorage.removeItem(blockedCartKey);
        cartMutated = true;
      } else if (qualifies && giftLines.length) {
        const updates = {};
        giftLines.forEach((item, index) => {
          updates[item.key] = index === 0 ? 1 : 0;
        });

        if (giftLines.length > 1 || giftLines[0].quantity !== 1) {
          latestCart = await updateLines(updates);
          cartMutated = true;
        } else {
          latestCart = cart;
        }

        sessionStorage.removeItem(blockedCartKey);
      } else if (qualifies && blockedFingerprint !== fingerprint) {
        try {
          await addGift(variantId);
          latestCart = await getCart();
          const addedGiftLines = getGiftLines(latestCart);

          if (!addedGiftLines.length) throw new Error('The promotional item was not added.');

          sessionStorage.removeItem(blockedCartKey);
          cartMutated = true;
        } catch (error) {
          blockCurrentCart(fingerprint, config.unavailableMessage);
          console.error('Free gift add failed.', error);
        }
      } else if (!qualifies) {
        sessionStorage.removeItem(blockedCartKey);
      }

      if (cartMutated) {
        await refreshCartUi(latestCart || (await getCart()));
      }
    } catch (error) {
      console.error('Free gift reconciliation failed.', error);
    } finally {
      syncing = false;
      if (queued) {
        queued = false;
        reconcile();
      }
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    restoreNotice();
    placeGiftRowsLast();
    reconcile();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        scheduleGiftRowPlacement();
        if (event.source !== 'free-gift') scheduleReconcile(0);
      });
    }

    document.addEventListener('change', (event) => {
      if (event.target.closest('cart-items, cart-drawer-items, form[action*="/cart"]')) {
        scheduleReconcile();
      }
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('cart-remove-button, [href*="/cart/change"], [data-cart-remove]')) {
        scheduleReconcile(750);
      }
    });

    const cartObserver = new MutationObserver((mutations) => {
      const cartChanged = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.('cart-items, cart-drawer-items, .cart-item, .cart__footer') ||
              node.querySelector?.('cart-items, cart-drawer-items, .cart-item, .cart__footer'))
        )
      );

      if (cartChanged) {
        scheduleGiftRowPlacement();
        scheduleReconcile();
      }
    });

    cartObserver.observe(document.body, { childList: true, subtree: true });
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      placeGiftRowsLast();
      reconcile();
    }
  });
})();
